/**
 * Admin · Feedback: every questionnaire answer in one place. Email surveys are
 * the one-click question and optional form linked from lifecycle emails
 * (`feedbackResponses`, see EMAIL.md); the NPS form is the older public page
 * at callout.jp/feedback?user=<uid> (`feedback_questionnaire`).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi, emailAdminApi, FeedbackListing, NpsFeedbackListing } from './adminApi';
import { AnswerBadge } from './AdminEmails';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

type Section = 'email' | 'nps';

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

// ---------------------------------------------------------------------------
// Email surveys
// ---------------------------------------------------------------------------

function Tally({ counts, total }: { counts: Record<string, number>; total: number }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const answered = entries.reduce((n, [, v]) => n + v, 0);
  return (
    <div className="admin-tabs" style={{ alignItems: 'center' }}>
      {entries.map(([k, v]) => (
        <span key={k}>
          <AnswerBadge value={k} /> {v}
          <span className="admin-muted"> ({Math.round((v / Math.max(answered, 1)) * 100)}%)</span>
        </span>
      ))}
      {total > answered && <span className="admin-muted">{total - answered} without an answer</span>}
    </div>
  );
}

function EmailSurveyView() {
  const [campaigns, setCampaigns] = useState<FeedbackListing['campaigns']>({});
  const [campaign, setCampaign] = useState('');
  const [listing, setListing] = useState<FeedbackListing | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setListing(null);
    emailAdminApi
      .feedback(campaign || undefined)
      .then((l) => {
        if (cancelled) return;
        setListing(l);
        if (!campaign) setCampaigns(l.campaigns);
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load feedback')));
    return () => {
      cancelled = true;
    };
  }, [campaign]);

  return (
    <div>
      <div className="admin-header">
        <select className="admin-search" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
          <option value="">All campaigns</option>
          {Object.entries(campaigns).map(([c, t]) => (
            <option key={c} value={c}>
              {c} ({t.total})
            </option>
          ))}
        </select>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {!listing ? (
        !error && <div className="admin-muted">Loading…</div>
      ) : (
        <>
          {!campaign && Object.keys(listing.campaigns).length > 0 && (
            <div className="admin-table-wrap" style={{ marginBottom: 20 }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Responses</th>
                    <th>Did Callout do what they needed?</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(listing.campaigns).map(([c, t]) => (
                    <tr key={c} onClick={() => setCampaign(c)}>
                      <td>
                        <strong>{c || '—'}</strong>
                      </td>
                      <td>{t.total}</td>
                      <td>
                        <Tally counts={t.metNeeds} total={t.total} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {campaign && (
            <div className="admin-section">
              <h2>
                {campaign}: {listing.total} response{listing.total === 1 ? '' : 's'}
              </h2>
              <Tally counts={listing.metNeeds} total={listing.total} />
            </div>
          )}
          {listing.responses.length === 0 ? (
            <div className="admin-muted">No responses yet.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table static">
                <thead>
                  <tr>
                    <th>Answered</th>
                    <th>Who</th>
                    {!campaign && <th>Campaign</th>}
                    <th>Answer</th>
                    <th>Use cases / issues</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {listing.responses.map((r) => (
                    <tr key={r.id}>
                      <td>{fmtAdminDateTime(r.updatedAt)}</td>
                      <td>
                        {r.userId ? (
                          <Link to={`/admin/users/${r.userId}`}>{r.email || r.userId}</Link>
                        ) : (
                          r.email || '—'
                        )}
                        {r.lang && <span className="admin-muted"> · {r.lang}</span>}
                      </td>
                      {!campaign && <td>{r.campaign || '—'}</td>}
                      <td>
                        <AnswerBadge value={r.metNeeds} />
                      </td>
                      <td className="wrap">
                        {[...(r.useCases || []), ...(r.issues || [])].join(', ') || '—'}
                      </td>
                      <td className="wrap email-comment">{r.comment || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NPS form
// ---------------------------------------------------------------------------

/** Promoters 9–10, passives 7–8, detractors 0–6. */
function npsTone(score: number): string {
  return score >= 9 ? 'positive' : score >= 7 ? 'partial' : 'negative';
}

function NpsBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="admin-muted">—</span>;
  return <span className={`answer-badge ${npsTone(score)}`}>{score}</span>;
}

function NpsView() {
  const [listing, setListing] = useState<NpsFeedbackListing | null>(null);
  const [error, setError] = useState('');
  const [usersOnly, setUsersOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .npsFeedback()
      .then((l) => !cancelled && setListing(l))
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load NPS responses')));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <div className="admin-error">{error}</div>;
  if (!listing) return <div className="admin-muted">Loading…</div>;

  const pct = (n: number) => `${Math.round((n / Math.max(listing.scored, 1)) * 100)}%`;
  const rows = usersOnly ? listing.responses.filter((r) => r.knownUser) : listing.responses;
  const strangers = listing.total - listing.fromUsers;

  return (
    <div>
      <div className="admin-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        <span className="answer-badge partial">unverified</span> Self-reported on the public form;
        anyone can submit it for any user ID. The score counts only responses from existing users.
      </div>
      <div className="admin-cards">
        <div className="admin-card">
          <div className="value">{listing.nps ?? '—'}</div>
          <div className="label">NPS ({listing.scored} scored)</div>
        </div>
        <div className="admin-card">
          <div className="value">{listing.promoters}</div>
          <div className="label">Promoters 9–10 ({pct(listing.promoters)})</div>
        </div>
        <div className="admin-card">
          <div className="value">{listing.passives}</div>
          <div className="label">Passives 7–8 ({pct(listing.passives)})</div>
        </div>
        <div className="admin-card">
          <div className="value">{listing.detractors}</div>
          <div className="label">Detractors 0–6 ({pct(listing.detractors)})</div>
        </div>
        <div className="admin-card">
          <div className="value">{listing.total}</div>
          <div className="label">
            Responses{strangers > 0 && ` (${strangers} not from a user)`}
          </div>
        </div>
      </div>

      {listing.scored > 0 && (
        <div className="admin-tabs" style={{ alignItems: 'center', marginBottom: 20 }}>
          {Object.entries(listing.distribution).map(([score, n]) => (
            <span key={score}>
              <NpsBadge score={Number(score)} /> {n}
            </span>
          ))}
        </div>
      )}

      {strangers > 0 && (
        <label className="admin-muted" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          <input type="checkbox" checked={usersOnly} onChange={(e) => setUsersOnly(e.target.checked)} />{' '}
          Only show existing users
        </label>
      )}

      {rows.length === 0 ? (
        <div className="admin-muted">No responses yet.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table static">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Who</th>
                <th>Score</th>
                <th>What they liked</th>
                <th>What to improve</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtAdminDateTime(r.submittedAt)}</td>
                  <td>
                    {r.knownUser && r.userId ? (
                      <Link to={`/admin/users/${r.userId}`}>{r.email || r.userId}</Link>
                    ) : (
                      <>
                        <span>{r.userId || '—'}</span>{' '}
                        <span className="answer-badge negative">not a user</span>
                      </>
                    )}
                  </td>
                  <td>
                    <NpsBadge score={r.npsScore} />
                  </td>
                  <td className="wrap email-comment">{r.liked || '—'}</td>
                  <td className="wrap email-comment">{r.improve || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

function AdminFeedback() {
  const [params, setParams] = useSearchParams();
  const section: Section = params.get('section') === 'nps' ? 'nps' : 'email';

  const sections: [Section, string][] = [
    ['email', 'Email surveys'],
    ['nps', 'NPS form'],
  ];

  return (
    <div className="admin-section" style={{ marginTop: 0 }}>
      <div className="admin-tabs" style={{ marginBottom: 16 }}>
        {sections.map(([key, label]) => (
          <button
            key={key}
            className={`admin-inline-btn ${section === key ? 'active' : ''}`}
            onClick={() =>
              setParams((prev) => {
                const next = new URLSearchParams(prev);
                // Also replaces the old ?tab=emails&section=feedback link.
                next.set('tab', 'feedback');
                next.set('section', key);
                return next;
              })
            }
          >
            {label}
          </button>
        ))}
      </div>

      {section === 'email' ? <EmailSurveyView /> : <NpsView />}
    </div>
  );
}

export default AdminFeedback;
